// src/app/store.ts
import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import { combineReducers } from "@reduxjs/toolkit";

// Import your reducers
import courseReducer from "../reducers/courses/courseSlice";
import authReducer from "../reducers/auth/authSlice";
import taskReducer from "../reducers/task/taskSlice";
import resourcesReducer from "../reducers/resources/resourceSlice";
import templatesReducer from "../reducers/qa/qaSlice";
import testReducer from "../reducers/test/testSlice";
import scheduleReducer from "../reducers/schedule/scheduleSlice";
import organizationReducer from "../reducers/organization/organizationSlice";
import userSlice from "../reducers/users/usersSlice";
import departmentSlice from "../reducers/departments/departmentSlice";

// Combine all reducers
const rootReducer = combineReducers({
  courses: courseReducer,
  auth: authReducer,
  tasks: taskReducer,
  resources: resourcesReducer,
  templates: templatesReducer,
  test: testReducer,
  schedules: scheduleReducer,
  organizations: organizationReducer,
  users: userSlice,
  departments: departmentSlice,
});

// Persist configuration
const persistConfig = {
  key: "root",
  storage,
  version: 1,
  // Whitelist - only persist these slices
  whitelist: [
    "auth", // Keep user authentication
    "organizations", // Keep organization data
    "departments", // Keep department data
  ],
  // Blacklist - don't persist these slices (they're temporary/should be fresh)
  blacklist: [
    "tasks", // Tasks should be fetched fresh
    "test", // Test data should be fresh
    "schedules", // Schedules should be fetched fresh
    "templates", // Templates might change server-side
    "resources", // Resources should be fresh
    "courses", // Courses should be fetched fresh
    "users", // user data should be fetched afresh
  ],
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          "auth/login/fulfilled",
          "auth/login/rejected",
        ],
        ignoredPaths: ["auth.error"],
      },
    }),
  devTools: process.env.NODE_ENV !== "production",
});

// Create persistor
export const persistor = persistStore(store);

// Types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
